import React, { useState, useEffect } from 'react';

// Import API
import { getVenueServices } from 'api';

// Import view component
import ServicesView from './view';

const ServicesContainer = ({
  // Data from parent
  selectedServices = [],
  
  // Handlers from parent
  onServicesChange,
  onServiceMoveUp,
  onServiceMoveDown, 
  onAddService,
  onRemoveService,
  onServiceDescriptionSave, // New prop for immediate description saves
  
  // UI props
  showSnackbar,
  disableSaveButton = false,
  isSaving = false // Add isSaving prop from parent
}) => {
  // Local UI state only - no business logic
  const [availableServices, setAvailableServices] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredServices, setFilteredServices] = useState([]);
  const [isLoadingServices, setIsLoadingServices] = useState(false);
  const [error, setError] = useState(null);
  const [editingService, setEditingService] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // Fetch available services for search dropdown
  useEffect(() => {
    const fetchServices = async () => {
      setIsLoadingServices(true);
      try {
        const response = await getVenueServices();
        if (response && response.data && response.data.results) {
          setAvailableServices(response.data.results);
        } else if (response && response.data) {
          setAvailableServices(response.data);
        } else if (Array.isArray(response)) {
          setAvailableServices(response);
        } else {
          setAvailableServices([]);
        }
      } catch (err) {
        setError('Failed to load available services');
        showSnackbar?.('Failed to load available services', 'error');
      } finally {
        setIsLoadingServices(false);
      }
    };
    fetchServices();
  }, [showSnackbar]);

  // Filter services based on search query
  useEffect(() => {
    if (searchQuery.trim().length > 0) {
      const query = searchQuery.toLowerCase().trim();
      const availableForSearch = availableServices.filter(service =>
        !selectedServices.some(selected => selected.slug === service.slug)
      );
      
      const filtered = availableForSearch.filter(service => {
        const name = service.name.toLowerCase();
        const desc = (service.description || '').toLowerCase();
        return name.includes(query) || desc.includes(query);
      });
      
      setFilteredServices(filtered);
      setShowDropdown(true);
    } else {
      setFilteredServices([]);
      setShowDropdown(false);
    }
  }, [searchQuery, availableServices, selectedServices]);

  // Search handlers
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleServiceSelect = (service) => {
    if (onAddService && !selectedServices.some(s => s.slug === service.slug)) {
      onAddService(service);
    }
    setSearchQuery('');
    setShowDropdown(false);
  };

  const handleClickAway = () => {
    setShowDropdown(false);
  };

  // Service action handlers - just pass through to parent
  const handleRemoveService = (serviceSlug) => {
    if (onRemoveService) {
      onRemoveService(serviceSlug);
    }
  };

  const movePriorityUp = (serviceSlug) => {
    if (onServiceMoveUp) {
      onServiceMoveUp(serviceSlug);
    }
  };

  const movePriorityDown = (serviceSlug) => {
    if (onServiceMoveDown) {
      onServiceMoveDown(serviceSlug);
    }
  };

  // Edit dialog handlers
  const startEditing = (service) => {
    setEditingService({ ...service });
    setEditDialogOpen(true);
  };

  const saveServiceChanges = async () => {
    if (editingService && onServicesChange) {
      const updatedServices = selectedServices.map(service =>
        service.slug === editingService.slug ? editingService : service
      );
      
      // Save immediately and show toast
      try {
        // Call parent's immediate save function if available
        if (onServiceDescriptionSave) {
          await onServiceDescriptionSave(editingService.slug, editingService.description);
          showSnackbar?.("Service description updated successfully", "success");
        } else {
          // Fallback to old behavior
          onServicesChange(updatedServices);
        }
        setEditingService(null);
        setEditDialogOpen(false);
      } catch (error) {
        showSnackbar?.("Failed to update service description: " + error.message, "error");
      }
    }
  };

  const cancelEditing = () => {
    setEditingService(null);
    setEditDialogOpen(false);
  };

  const handleDescriptionChange = (e) => {
    if (editingService) {
      setEditingService({ ...editingService, description: e.target.value });
    }
  };

  return (
    <ServicesView
      selectedServices={selectedServices}
      searchQuery={searchQuery}
      showDropdown={showDropdown}
      filteredServices={filteredServices}
      isSaving={isSaving} // Pass through from parent
      isLoadingServices={isLoadingServices}
      hasChanges={false} // No more internal change tracking
      error={error}
      editingService={editingService}
      editDialogOpen={editDialogOpen}
      disableSaveButton={disableSaveButton}
      handleSearchChange={handleSearchChange}
      handleServiceSelect={handleServiceSelect}
      handleRemoveService={handleRemoveService}
      handleSaveServices={() => {}} // No-op since parent handles saving
      movePriorityUp={movePriorityUp}
      movePriorityDown={movePriorityDown}
      startEditing={startEditing}
      saveServiceChanges={saveServiceChanges}
      cancelEditing={cancelEditing}
      handleDescriptionChange={handleDescriptionChange}
      handleClickAway={handleClickAway}
    />
  );
};

export default ServicesContainer;